// P13 — Dashboard de uso por agente (admin).
//
// Cards de totais (conversas, mensagens, custo lifetime, top user) +
// gráfico tokens/dia 30d (recharts via LineChartCard) + tabela top users +
// lista das últimas 50 conversas (decisão G5 — clica abre o conteúdo completo
// num modal) + section de erros recentes (FAILED/EXHAUSTED).

import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import adminAgentsService from '@/services/adminAgents.service';
import { useToast } from '@/contexts/ToastContext';
import { UsageDashboard } from '@/types/agents';
import LineChartCard from '@/components/admin/charts/LineChartCard';
import ConversationViewerModal from '@/components/admin/ConversationViewerModal';

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtUsd(v: number, digits = 4): string {
  return `$${v.toFixed(digits)}`;
}

function fmtNumber(v: number): string {
  return v.toLocaleString('pt-BR');
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-secondary p-4">
      <p className="text-xs uppercase tracking-wide text-content-tertiary">{label}</p>
      <p className="text-2xl font-bold text-content-primary mt-1">{value}</p>
      {hint && <p className="text-xs text-content-tertiary mt-1">{hint}</p>}
    </div>
  );
}

const AdminAgentUsagePage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [data, setData] = useState<UsageDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedConv, setSelectedConv] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    adminAgentsService
      .getUsage(id)
      .then(setData)
      .catch(() => showToast('Não foi possível carregar o dashboard.', 'error'))
      .finally(() => setLoading(false));
  }, [id, showToast]);

  if (!id) return null;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-agent-usage-page">
      <header className="mb-6">
        <button
          type="button"
          onClick={() => navigate(`/admin/agentes/${id}/editar`)}
          className="text-sm text-content-tertiary hover:text-content-primary mb-2"
        >
          ← Voltar pra detalhe do agente
        </button>
        <h1 className="text-2xl font-bold text-content-primary">Uso do agente</h1>
        <p className="text-sm text-content-tertiary mt-1">
          Tokens, custo e conteúdo das conversas. Atualiza a cada carregamento da página.
        </p>
      </header>

      {loading && <p className="text-sm text-content-tertiary">Carregando…</p>}

      {!loading && data && (
        <div className="space-y-6">
          {/* Cards de totais */}
          <section
            className="grid grid-cols-2 md:grid-cols-4 gap-3"
            data-testid="totals-cards"
          >
            <StatCard
              label="Conversas (lifetime)"
              value={fmtNumber(data.totals.total_conversations)}
            />
            <StatCard
              label="Mensagens (lifetime)"
              value={fmtNumber(data.totals.total_messages)}
            />
            <StatCard
              label="Custo USD (lifetime)"
              value={fmtUsd(data.totals.total_cost_lifetime, 2)}
              hint="Soma de todo o AgentUsageLog"
            />
            <StatCard
              label="Top user"
              value={data.top_users[0]?.name ?? '—'}
              hint={
                data.top_users[0]
                  ? `${fmtNumber(data.top_users[0].total_tokens)} tokens`
                  : 'Sem uso ainda'
              }
            />
          </section>

          {/* Chart tokens/dia */}
          <section data-testid="daily-usage-chart">
            <LineChartCard
              title="Tokens por dia (últimos 30 dias)"
              subtitle="Soma de tokens_in + tokens_out por dia"
              data={data.daily_usage.map((d) => ({ date: d.date, tokens: d.tokens_total }))}
              dataKey="tokens"
              xKey="date"
              yLabel="tokens"
              height={300}
            />
          </section>

          {/* Top users */}
          <section
            className="rounded-xl border border-surface-border bg-surface-secondary"
            data-testid="top-users-section"
          >
            <header className="px-4 py-3 border-b border-surface-border">
              <h2 className="text-sm font-semibold text-content-primary">Top 10 usuários (lifetime)</h2>
            </header>
            {data.top_users.length === 0 ? (
              <p className="text-sm text-content-tertiary p-4">Sem uso ainda.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-primary">
                    <tr>
                      <th className="text-left px-4 py-2 text-content-tertiary font-medium">Usuário</th>
                      <th className="text-left px-4 py-2 text-content-tertiary font-medium">Email</th>
                      <th className="text-right px-4 py-2 text-content-tertiary font-medium">Tokens</th>
                      <th className="text-right px-4 py-2 text-content-tertiary font-medium">Custo USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_users.map((u) => (
                      <tr key={u.user_id} className="border-t border-surface-border">
                        <td className="px-4 py-2 text-content-primary">{u.name ?? '—'}</td>
                        <td className="px-4 py-2 text-content-tertiary">{u.email ?? '—'}</td>
                        <td className="px-4 py-2 text-right text-content-primary">
                          {fmtNumber(u.total_tokens)}
                        </td>
                        <td className="px-4 py-2 text-right text-content-primary">
                          {fmtUsd(u.total_cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Conversas (decisão G5) */}
          <section
            className="rounded-xl border border-surface-border bg-surface-secondary"
            data-testid="conversations-section"
          >
            <header className="px-4 py-3 border-b border-surface-border">
              <h2 className="text-sm font-semibold text-content-primary">
                Últimas 50 conversas
              </h2>
              <p className="text-xs text-amber-300 mt-1">
                ⚠ Admin tem visibilidade do conteúdo completo. Use com responsabilidade.
              </p>
            </header>
            {data.conversations.length === 0 ? (
              <p className="text-sm text-content-tertiary p-4">Nenhuma conversa ainda.</p>
            ) : (
              <ul>
                {data.conversations.map((c) => (
                  <li key={c.id} className="border-t border-surface-border first:border-t-0">
                    <button
                      type="button"
                      onClick={() => setSelectedConv(c.id)}
                      data-testid={`conv-${c.id}`}
                      className="w-full text-left px-4 py-2.5 hover:bg-surface-primary flex items-center gap-3"
                    >
                      <span className="flex-1 min-w-0">
                        <span className="font-semibold text-content-primary text-sm">
                          {c.user_name ?? '—'}
                        </span>
                        <span className="text-content-tertiary text-sm"> · {c.title}</span>
                      </span>
                      <span className="text-xs text-content-tertiary whitespace-nowrap">
                        {fmtDate(c.last_message_at || c.created_at)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Erros */}
          {data.errors.length > 0 && (
            <section
              className="rounded-xl border border-red-500/30 bg-red-500/5"
              data-testid="errors-section"
            >
              <header className="px-4 py-3 border-b border-red-500/30">
                <h2 className="text-sm font-semibold text-red-200">
                  Erros recentes ({data.errors.length})
                </h2>
              </header>
              <ul className="divide-y divide-red-500/20">
                {data.errors.map((e) => (
                  <li key={e.id} className="p-4 text-sm">
                    <div className="flex justify-between gap-2">
                      <strong className="text-red-200 text-xs uppercase">{e.status}</strong>
                      <span className="text-xs text-content-tertiary">{fmtDate(e.created_at)}</span>
                    </div>
                    <p className="text-content-primary mt-1">
                      <span className="text-content-tertiary">User: </span>
                      {e.user_name ?? '—'}
                    </p>
                    <p className="text-content-primary text-xs mt-1">
                      <span className="text-content-tertiary">Pergunta: </span>"
                      {e.user_message_content?.slice(0, 200)}"
                    </p>
                    {e.error_message && (
                      <p className="text-red-300 text-xs mt-1 font-mono break-all">
                        {e.error_message}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {selectedConv && id && (
        <ConversationViewerModal
          agentId={id}
          conversationId={selectedConv}
          onClose={() => setSelectedConv(null)}
        />
      )}
    </div>
  );
};

export default AdminAgentUsagePage;
